/**
 * ChangeEmailSection Component
 * Change email address with OTP verification
 */

import { useEffect, useRef, useState } from "react"
import { toast } from "sonner"
import { CustomInput } from "@/components/forms/customInput"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { useAuthStore } from "@/features/auth/store/authStore"
import { useRefreshUserProfile } from "@/features/profile/hooks/useRefreshUserProfile"
import { useTranslation } from "@/language"
import { cn } from "@/lib/utils"
import * as settingsService from "../services/settings.service"

export function ChangeEmailSection() {
  const { t } = useTranslation("settings")
  const user = useAuthStore(state => state.user)
  const { refreshUserProfile } = useRefreshUserProfile()
  const [newEmail, setNewEmail] = useState("")

  // Verification state
  const [isVerifying, setIsVerifying] = useState(false)
  const [isSendingOtp, setIsSendingOtp] = useState(false)
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false)
  const [resendCountdown, setResendCountdown] = useState(0)
  const [otpError, setOtpError] = useState("")
  const [otp, setOtp] = useState("")

  // OTP refs for focus management
  const otpInputRefs = useRef<(HTMLInputElement | null)[]>([])

  const currentEmail = user?.email || t("changeEmail.currentEmailNotSet")

  // Countdown timer for resend
  useEffect(() => {
    if (resendCountdown > 0) {
      const timer = setTimeout(() => setResendCountdown(resendCountdown - 1), 1000)
      return () => clearTimeout(timer)
    }
  }, [resendCountdown])

  // Auto-focus first OTP input when verification screen appears
  useEffect(() => {
    if (isVerifying && otpInputRefs.current[0]) {
      otpInputRefs.current[0]?.focus()
    }
  }, [isVerifying])

  // Validate email format
  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  // Handle sending OTP
  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!newEmail.trim()) {
      toast.error(t("changeEmail.validationEnterEmail"))
      return
    }

    if (!isValidEmail(newEmail)) {
      toast.error(t("changeEmail.validationValidEmail"))
      return
    }

    // Check if email is same as current
    if (newEmail.toLowerCase() === currentEmail.toLowerCase()) {
      toast.error(t("changeEmail.validationDifferentEmail"))
      return
    }

    setIsSendingOtp(true)
    setOtpError("")

    try {
      await settingsService.sendEmailVerificationOtp(newEmail)
      setIsVerifying(true)
      setResendCountdown(60)
      toast.success(t("changeEmail.successCodeSent"))
    } catch (error) {
      const err = error as { response?: { data?: { message?: string } } }
      const message = err.response?.data?.message || t("changeEmail.errorFailedToSend")
      toast.error(message)
    } finally {
      setIsSendingOtp(false)
    }
  }

  // Handle OTP verification
  const handleVerifyOtp = async (otpValue: string) => {
    setIsVerifyingOtp(true)
    setOtpError("")

    try {
      await settingsService.verifyEmailOtp(otpValue, newEmail)
      toast.success(t("changeEmail.successEmailChanged"))

      // Refresh user profile to get updated email
      await refreshUserProfile()

      // Reset state
      setIsVerifying(false)
      setNewEmail("")
    } catch (error) {
      const err = error as { response?: { data?: { message?: string } } }
      const message = err.response?.data?.message || t("changeEmail.errorInvalidCode")
      setOtpError(message)
      throw new Error(message)
    } finally {
      setIsVerifyingOtp(false)
    }
  }

  // Handle resend OTP
  const handleResendOtp = async () => {
    if (resendCountdown > 0) return

    setIsSendingOtp(true)
    setOtpError("")

    try {
      await settingsService.sendEmailVerificationOtp(newEmail)
      setResendCountdown(60)
      toast.success(t("changeEmail.successCodeSent"))
    } catch (error) {
      const err = error as { response?: { data?: { message?: string } } }
      const message = err.response?.data?.message || t("changeEmail.errorFailedToSend")
      toast.error(message)
    } finally {
      setIsSendingOtp(false)
    }
  }

  // OTP handlers
  const isOtpComplete = otp.length === 6

  const handleOtpChange = (index: number, value: string) => {
    // Only allow numbers
    if (!/^\d*$/.test(value)) return

    // Take only the last character
    const digit = value.slice(-1)

    // Update the value
    const newValue = otp.split("")
    newValue[index] = digit
    const result = newValue.join("").slice(0, 6)
    setOtp(result)

    // Auto-focus next input
    if (digit && index < 5) {
      otpInputRefs.current[index + 1]?.focus()
    }

    // Auto-submit when OTP is complete
    if (result.length === 6) {
      handleVerifyOtp(result).catch(() => {
        // Error is already handled in handleVerifyOtp
      })
    }
  }

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      // Focus previous input
      otpInputRefs.current[index - 1]?.focus()
    }
  }

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData("text/plain")

    // Extract only digits
    const digits = pastedData.replace(/\D/g, "").slice(0, 6)
    setOtp(digits)

    if (digits.length === 6) {
      handleVerifyOtp(digits).catch(() => {
        // Error is already handled in handleVerifyOtp
      })
    }
  }

  const handleResendCode = () => {
    if (resendCountdown > 0) return
    setOtp("")
    handleResendOtp()
  }

  // If in verification mode, show verification screen inline
  if (isVerifying) {
    return (
      <div className="flex flex-col px-4 py-6">
        {/* Main Content */}
        <div className="flex-1 flex items-center justify-center">
          <div className="w-full max-w-md">
            {/* Title */}
            <div className="text-center mb-3">
              <h2 className="text-xl font-semibold text-primary">{t("changeEmail.verifyTitle")}</h2>
            </div>

            {/* Subtitle */}
            <div className="text-center mb-8 space-y-1">
              <p className="text-sm text-body">{t("changeEmail.emailSent", { email: newEmail })}</p>
              <p className="text-sm text-body">{t("changeEmail.enterCode")}</p>
            </div>

            {/* OTP Input */}
            <div className="flex justify-center gap-1 mb-8">
              {[0, 1, 2, 3, 4, 5].map(index => (
                <input
                  key={index}
                  ref={el => {
                    otpInputRefs.current[index] = el
                  }}
                  id={`otp-${index}`}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={otp[index] || ""}
                  onChange={e => handleOtpChange(index, e.target.value)}
                  onKeyDown={e => handleKeyDown(index, e)}
                  onPaste={handlePaste}
                  disabled={isVerifyingOtp}
                  className={cn(
                    "w-12 h-12 text-center text-xl font-semibold",
                    "border-2 rounded-lg",
                    "focus:outline-none",
                    "disabled:cursor-not-allowed disabled:opacity-50",
                    "transition-all duration-200",
                    otpError
                      ? "border-brand-terracotta focus:border-brand-terracotta focus:ring-brand-terracotta"
                      : "border-1 border-input focus:border-input focus:ring-2 focus:ring-charcoal",
                  )}
                  style={{
                    fontSize: "18px",
                    fontFamily: "inherit",
                  }}
                />
              ))}
            </div>

            {/* Error Message */}
            {otpError && (
              <div className="text-center mb-6">
                <p className="text-sm text-brand-terracotta font-medium">{otpError}</p>
              </div>
            )}

            {/* Resend Link */}
            <div className="text-center mb-8">
              <p className="text-sm text-body">
                {t("changeEmail.didntGetCode")}{" "}
                {resendCountdown > 0 ? (
                  <span className="text-primary">
                    {t("changeEmail.resendCountdown", { resendCountdown })}
                  </span>
                ) : (
                  <Button
                    type="button"
                    variant="link"
                    onClick={handleResendCode}
                    className="font-semibold underline hover:no-underline p-0 h-auto text-charcoal"
                    disabled={isSendingOtp}
                  >
                    {t("changeEmail.resend")}
                  </Button>
                )}
              </p>
            </div>

            {/* Continue Button */}
            <div className="flex justify-center">
              <Button
                type="button"
                variant="primary"
                className="min-w-[200px] h-12 px-8 text-base font-semibold rounded-lg"
                disabled={!isOtpComplete || isVerifyingOtp}
                isLoading={isVerifyingOtp}
                onClick={() => handleVerifyOtp(otp)}
              >
                {t("changeEmail.continue")}
              </Button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-7.5">
      {/* Section Header */}
      <div>
        <h1 className="text-lg md:text-xl leading-5 font-semibold text-primary uppercase section-heading">
          {t("changeEmail.title")}
        </h1>
        <p className="text-sm text-primary leading-6.25 mt-2.5 md:mt-1.25">
          {t("changeEmail.description")}
        </p>
      </div>

      <form onSubmit={handleSendOtp} className="flex flex-col gap-7.5 max-w-lg">
        {/* Current Email */}
        <div className="flex flex-col md:flex-row md:items-center gap-1.25 md:gap-2.5 w-full text-sm text-primary">
          <Label className="leading-6.25">{t("changeEmail.currentEmail")}</Label>
          <p className="font-semibold leading-6.25">{currentEmail}</p>
        </div>

        <div className="flex flex-col xl:flex-row xl:items-end gap-5">
          {/* New Email */}
          <CustomInput
            id="new-email"
            required={true}
            label={t("changeEmail.newEmailLabel")}
            className="w-full md:w-[362px]"
            type="email"
            value={newEmail}
            onChange={e => setNewEmail(e.target.value)}
            placeholder={t("changeEmail.newEmailPlaceholder")}
          />

          <div>
            <Button
              type="submit"
              variant="primary"
              size="md"
              className="py-3.75 w-full xl:w-[220px] uppercase"
              isLoading={isSendingOtp}
              disabled={!newEmail.trim() || !isValidEmail(newEmail)}
            >
              {t("changeEmail.change")}
            </Button>
          </div>
        </div>
      </form>
    </div>
  )
}
